import API_BASE from '../../utils/api';
import React, { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import Sidebar from "../../components/Sidebar";
import ProfileDropdown from "../../components/ProfileDropdown";
import { fetchDefaultAcademicYear, computedAcademicYearFallback } from "../../utils/academicYear";

const IconEye = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
  </svg>
);
const IconEdit = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);
const IconTrash = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const ADD_NEW = '__add_new__';

const CLASS_NAME_OPTIONS = [
  { value: 'PP1', label: 'PP1 (Pre-Primary 1 / Nursery) — 3-4 Years' },
  { value: 'PP2', label: 'PP2 (Pre-Primary 2 / LKG) — 4-5 Years' },
  { value: 'UKG', label: 'UKG (Upper Kindergarten) — 5-6 Years' },
  { value: '1', label: 'Class 1 — 6-7 Years' },
  { value: '2', label: 'Class 2 — 7-8 Years' },
  { value: '3', label: 'Class 3 — 8-9 Years' },
  { value: '4', label: 'Class 4 — 9-10 Years' },
  { value: '5', label: 'Class 5 — 10-11 Years' },
  { value: '6', label: 'Class 6 — 11-12 Years' },
  { value: '7', label: 'Class 7 — 12-13 Years' },
  { value: '8', label: 'Class 8 — 13-14 Years' },
  { value: '9', label: 'Class 9 — 14-15 Years' },
  { value: '10', label: 'Class 10 — 15-16 Years' },
];

const SECTION_OPTIONS = 'ABCDEFGHIJKLM'.split('');

const ALL_SUBJECTS = [
  'English', 'Mathematics', 'Science', 'Social Studies', 'Hindi', 'Telugu',
  'Computer Science', 'Environmental Science (EVS)', 'General Knowledge',
  'Art & Craft', 'Physical Education', 'Music', 'Moral Science'
];

const AdminClasses = () => {
  const { user } = useAuth();
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [viewingClass, setViewingClass] = useState(null);
  const [editingClass, setEditingClass] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  // Seeded with the computed fallback for the very first paint; replaced by
  // the school's configured Settings → Academic Year as soon as it loads.
  const [defaultAcademicYear, setDefaultAcademicYear] = useState(computedAcademicYearFallback());
  const [formData, setFormData] = useState({
    className: "",
    section: "",
    classTeacherId: "",
    academicYear: defaultAcademicYear,
    subjects: [],
  });
  const [customClassMode, setCustomClassMode] = useState(false);
  const [customSectionMode, setCustomSectionMode] = useState(false);
  const [showAddSubject, setShowAddSubject] = useState(false);
  const [newSubjectName, setNewSubjectName] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importError, setImportError] = useState('');

  useEffect(() => {
    fetchClasses();
    fetchTeachers();
    fetchDefaultAcademicYear().then(setDefaultAcademicYear);
  }, []);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_BASE}/classes`);
      setClasses(response.data);
    } catch (error) {
      console.error("Error fetching classes:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTeachers = async () => {
    try {
      const response = await axios.get(`${API_BASE}/teachers`);
      setTeachers(response.data);
    } catch (error) {
      console.error("Error fetching teachers:", error);
    }
  };

  const validateForm = () => {
    const errs = {};
    if (!formData.className.trim()) errs.className = "Class name is required";
    if (!formData.section.trim()) errs.section = "Section is required";
    if (!formData.academicYear.trim()) errs.academicYear = "Academic year is required";
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      if (editingClass) {
        await axios.put(`${API_BASE}/classes/${editingClass._id}`, formData);
      } else {
        await axios.post(`${API_BASE}/classes`, formData);
      }
      setShowModal(false);
      setEditingClass(null);
      resetForm();
      fetchClasses();
    } catch (error) {
      alert(error.response?.data?.error || "Error saving class");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this class?")) {
      try {
        await axios.delete(`${API_BASE}/classes/${id}`);
        fetchClasses();
      } catch (error) {
        alert(error.response?.data?.error || "Error deleting class");
      }
    }
  };

  const handleEdit = (cls) => {
    setEditingClass(cls);
    setFormData({
      className: cls.className,
      section: cls.section,
      classTeacherId: cls.classTeacherId?._id || "",
      academicYear: cls.academicYear,
      subjects: cls.subjects || [],
    });
    setCustomClassMode(!!cls.className && !CLASS_NAME_OPTIONS.some(o => o.value === cls.className));
    setCustomSectionMode(!!cls.section && !SECTION_OPTIONS.includes(cls.section));
    setShowAddSubject(false);
    setNewSubjectName("");
    setFormErrors({});
    setShowModal(true);
  };

  const addNewSubject = () => {
    const name = newSubjectName.trim();
    if (!name) return;
    if (!formData.subjects.some(s => s.subjectName.toLowerCase() === name.toLowerCase())) {
      setFormData(f => ({ ...f, subjects: [...f.subjects, { subjectName: name }] }));
    }
    setNewSubjectName("");
    setShowAddSubject(false);
  };

  const toggleSubject = (subjectName) => {
    setFormData(f => {
      const exists = f.subjects.some(s => s.subjectName === subjectName);
      return {
        ...f,
        subjects: exists
          ? f.subjects.filter(s => s.subjectName !== subjectName)
          : [...f.subjects, { subjectName }]
      };
    });
  };

  const resetForm = () => {
    setFormData({
      className: "",
      section: "",
      classTeacherId: "",
      academicYear: defaultAcademicYear,
      subjects: [],
    });
    setCustomClassMode(false);
    setCustomSectionMode(false);
    setShowAddSubject(false);
    setNewSubjectName("");
    setFormErrors({});
  };

  const downloadTemplate = () => {
    const header = 'ClassName,Section,AcademicYear';
    const sample = `1,A,${formData.academicYear}\n2,B,${formData.academicYear}`;
    const blob = new Blob([`${header}\n${sample}\n`], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'class_import_template.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const handleExport = (format) => {
    setShowExportMenu(false);
    if (format === 'json') {
      const data = classes.map(cls => ({
        className: cls.className,
        section: cls.section,
        academicYear: cls.academicYear,
        classTeacher: cls.classTeacherId?.name || '',
        students: cls.students?.length || 0,
        subjects: cls.subjects?.map(s => s.subjectName).join(', ') || ''
      }));
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'classes.json'; a.click();
      URL.revokeObjectURL(url);
    } else if (format === 'xlsx') {
      const header = 'Class Name,Section,Academic Year,Class Teacher,Students,Subjects';
      const rows = classes.map(cls => [
        cls.className, cls.section, cls.academicYear,
        cls.classTeacherId?.name || '',
        cls.students?.length || 0,
        cls.subjects?.map(s => s.subjectName).join('; ') || ''
      ].map(v => `"${String(v).replace(/"/g, '""')}"`).join(','));
      const csv = [header, ...rows].join('\n');
      const blob = new Blob([csv], { type: 'application/vnd.ms-excel' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = 'classes.xlsx'; a.click();
      URL.revokeObjectURL(url);
    } else if (format === 'pdf') {
      const win = window.open('', '_blank');
      const rows = classes.map(cls => `<tr>
        <td>${cls.className}</td><td>${cls.section}</td><td>${cls.academicYear}</td>
        <td>${cls.classTeacherId?.name || '—'}</td><td>${cls.students?.length || 0}</td>
        <td>${cls.subjects?.map(s => s.subjectName).join(', ') || '—'}</td>
      </tr>`).join('');
      win.document.write(`<!DOCTYPE html><html><head><title>Classes Export</title>
        <style>body{font-family:Arial;padding:20px}table{width:100%;border-collapse:collapse}
        th,td{border:1px solid #ccc;padding:8px;text-align:left;font-size:13px}
        th{background:#4f46e5;color:#fff}tr:nth-child(even){background:#f5f5f5}
        h2{color:#1e1b4b}p{color:#666;font-size:12px}</style></head>
        <body><h2>Class Management Export</h2><p>Generated: ${new Date().toLocaleDateString()}</p>
        <table><thead><tr><th>Class</th><th>Section</th><th>Academic Year</th>
        <th>Class Teacher</th><th>Students</th><th>Subjects</th></tr></thead>
        <tbody>${rows}</tbody></table></body></html>`);
      win.document.close(); win.print();
    }
  };

  const handleBulkImport = async () => {
    if (!importFile) return;
    setImporting(true);
    setImportError('');
    setImportResult(null);
    try {
      const text = await importFile.text();
      const lines = text.trim().split('\n').filter(l => l.trim());
      if (lines.length < 2) { setImportError('File must have a header row and at least one data row.'); setImporting(false); return; }
      const headers = lines[0].toLowerCase().split(',').map(h => h.trim().replace(/^"|"$/g, ''));
      let created = 0, failed = 0;
      const results = [];
      for (let i = 1; i < lines.length; i++) {
        const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
        const row = {};
        headers.forEach((h, idx) => { row[h] = values[idx] || ''; });
        const className = row['classname'] || row['class name'] || row['class'] || '';
        const section = row['section'] || '';
        const academicYear = row['academicyear'] || row['academic year'] || row['academic_year'] || formData.academicYear;
        if (!className || !section) {
          failed++;
          results.push({ row: i + 1, status: 'error', error: 'Missing ClassName or Section' });
          continue;
        }
        try {
          await axios.post(`${API_BASE}/classes`, { className, section, academicYear });
          created++;
          results.push({ row: i + 1, status: 'success' });
        } catch (err) {
          failed++;
          results.push({ row: i + 1, status: 'error', error: err.response?.data?.error || 'Failed to create' });
        }
      }
      setImportResult({ total: lines.length - 1, created, failed, results });
      fetchClasses();
    } catch (err) {
      setImportError('Failed to parse file. Ensure it is a valid CSV.');
    } finally {
      setImporting(false);
    }
  };

  const filtered = classes.filter(cls =>
    cls.className?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cls.section?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cls.classTeacherId?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cls.academicYear?.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 bg-gray-100 flex items-center justify-center h-screen">
          <div className="text-xl">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-100">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <nav className="bg-white shadow-sm sticky top-0 z-10">
          <div className="px-6 py-4 flex justify-between items-center">
            <h1 className="text-2xl font-semibold text-gray-800">Class Management</h1>
            <ProfileDropdown />
          </div>
        </nav>

        <div className="flex-1 overflow-auto p-6">
          {/* Toolbar */}
          <div className="bg-white rounded-lg shadow p-4 mb-5 flex flex-col sm:flex-row gap-3 items-center justify-between">
            <input
              type="text"
              placeholder="Search by class, section, teacher, year..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="flex-1 border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <div className="relative">
                <button
                  onClick={() => setShowExportMenu(s => !s)}
                  className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2 whitespace-nowrap"
                >
                  Export ▾
                </button>
                {showExportMenu && (
                  <div className="absolute right-0 mt-1 w-36 bg-white border rounded-lg shadow-lg z-20 overflow-hidden">
                    <button onClick={() => handleExport("xlsx")} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">Export XLS</button>
                    <button onClick={() => handleExport("pdf")} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">Export PDF</button>
                    <button onClick={() => handleExport("json")} className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50">Export JSON</button>
                  </div>
                )}
              </div>
              <button
                onClick={() => { setImportFile(null); setImportResult(null); setImportError(''); setShowImportModal(true); }}
                className="bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 flex items-center gap-2 whitespace-nowrap"
              >
                Bulk Upload
              </button>
              <button
                onClick={() => { resetForm(); setEditingClass(null); setShowModal(true); }}
                className="bg-indigo-600 text-white px-5 py-2 rounded-lg hover:bg-indigo-700 flex items-center gap-2 whitespace-nowrap"
              >
                <span className="text-lg leading-none">+</span> Add New Class
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">#</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Class</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Section</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Academic Year</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Class Teacher</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Students</th>
                    <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Subjects</th>
                    <th className="px-5 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                        <div className="text-4xl mb-2">🏫</div>
                        <p>{searchTerm ? "No classes match your search." : 'No classes found. Click "Add New Class" to create one.'}</p>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((cls, idx) => (
                      <tr key={cls._id} className="hover:bg-gray-50 transition">
                        <td className="px-5 py-4 text-sm text-gray-500">{idx + 1}</td>
                        <td className="px-5 py-4 font-semibold text-gray-800">{cls.className}</td>
                        <td className="px-5 py-4">
                          <span className="px-2 py-1 bg-indigo-100 text-indigo-800 rounded-full text-xs font-medium">{cls.section}</span>
                        </td>
                        <td className="px-5 py-4 text-sm text-gray-600">{cls.academicYear}</td>
                        <td className="px-5 py-4 text-sm text-gray-700">
                          {cls.classTeacherId?.name || <span className="text-gray-400 italic">Not Assigned</span>}
                        </td>
                        <td className="px-5 py-4">
                          <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                            {cls.students?.length || 0}
                          </span>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex flex-wrap gap-1 max-w-[180px]">
                            {cls.subjects?.slice(0, 3).map((s, i) => (
                              <span key={i} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">{s.subjectName}</span>
                            ))}
                            {cls.subjects?.length > 3 && (
                              <span className="px-2 py-0.5 bg-gray-200 text-gray-600 rounded text-xs">+{cls.subjects.length - 3}</span>
                            )}
                          </div>
                        </td>
                        <td className="px-5 py-4">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              onClick={() => setViewingClass(cls)}
                              title="View Details"
                              className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded-lg transition"
                            >
                              <IconEye />
                            </button>
                            <button
                              onClick={() => handleEdit(cls)}
                              title="Edit"
                              className="p-1.5 text-amber-600 hover:bg-amber-50 rounded-lg transition"
                            >
                              <IconEdit />
                            </button>
                            <button
                              onClick={() => handleDelete(cls._id)}
                              title="Delete"
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg transition"
                            >
                              <IconTrash />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {filtered.length > 0 && (
              <div className="px-5 py-3 border-t text-sm text-gray-500">
                Showing {filtered.length} of {classes.length} classes
              </div>
            )}
          </div>
        </div>
      </div>

      {/* View Details Modal */}
      {viewingClass && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="bg-indigo-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h2 className="text-xl font-bold text-white">
                Class {viewingClass.className} – {viewingClass.section}
              </h2>
              <button onClick={() => setViewingClass(null)} className="text-white text-2xl hover:text-indigo-200">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Academic Year</p>
                  <p className="font-semibold text-gray-800">{viewingClass.academicYear}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-xs text-gray-500 mb-1">Total Students</p>
                  <p className="font-semibold text-gray-800">{viewingClass.students?.length || 0}</p>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-xs text-gray-500 mb-1">Class Teacher</p>
                <p className="font-semibold text-gray-800">{viewingClass.classTeacherId?.name || "Not Assigned"}</p>
                {viewingClass.classTeacherId?.email && (
                  <p className="text-sm text-gray-500">{viewingClass.classTeacherId.email}</p>
                )}
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-2">Subjects ({viewingClass.subjects?.length || 0})</p>
                {viewingClass.subjects?.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {viewingClass.subjects.map((s, i) => (
                      <span key={i} className="px-3 py-1 bg-indigo-100 text-indigo-800 rounded-full text-sm">{s.subjectName}</span>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 italic text-sm">No subjects added</p>
                )}
              </div>
            </div>
            <div className="px-6 pb-4 flex justify-end gap-2">
              <button
                onClick={() => { setViewingClass(null); handleEdit(viewingClass); }}
                className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition text-sm"
              >
                Edit Class
              </button>
              <button onClick={() => setViewingClass(null)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition text-sm">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Class Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="bg-indigo-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">
                {editingClass ? "Edit Class" : "Add New Class"}
              </h3>
              <button onClick={() => { setShowModal(false); setEditingClass(null); }} className="text-white text-2xl hover:text-indigo-200">✕</button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Class Name *</label>
                  <select
                    required={!customClassMode}
                    value={customClassMode ? ADD_NEW : formData.className}
                    onChange={e => {
                      const v = e.target.value;
                      if (v === ADD_NEW) {
                        setCustomClassMode(true);
                        setFormData(f => ({ ...f, className: "" }));
                      } else {
                        setCustomClassMode(false);
                        setFormData(f => ({ ...f, className: v }));
                      }
                    }}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${formErrors.className ? 'border-red-400 focus:ring-red-400' : 'focus:ring-indigo-500'}`}
                  >
                    <option value="" disabled>Select class name</option>
                    {CLASS_NAME_OPTIONS.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                    <option value={ADD_NEW}>+ Add New</option>
                  </select>
                  {customClassMode && (
                    <input
                      type="text"
                      required
                      autoFocus
                      value={formData.className}
                      onChange={e => setFormData(f => ({ ...f, className: e.target.value }))}
                      className={`w-full border rounded-lg px-3 py-2 mt-2 focus:outline-none focus:ring-2 ${formErrors.className ? 'border-red-400 focus:ring-red-400' : 'focus:ring-indigo-500'}`}
                      placeholder="Enter new class name"
                    />
                  )}
                  {formErrors.className && <p className="text-xs text-red-500 mt-1">{formErrors.className}</p>}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section *</label>
                  <select
                    required={!customSectionMode}
                    value={customSectionMode ? ADD_NEW : formData.section}
                    onChange={e => {
                      const v = e.target.value;
                      if (v === ADD_NEW) {
                        setCustomSectionMode(true);
                        setFormData(f => ({ ...f, section: "" }));
                      } else {
                        setCustomSectionMode(false);
                        setFormData(f => ({ ...f, section: v }));
                      }
                    }}
                    className={`w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 ${formErrors.section ? 'border-red-400 focus:ring-red-400' : 'focus:ring-indigo-500'}`}
                  >
                    <option value="" disabled>Select section</option>
                    {SECTION_OPTIONS.map(sec => (
                      <option key={sec} value={sec}>{sec}</option>
                    ))}
                    <option value={ADD_NEW}>+ Add New</option>
                  </select>
                  {customSectionMode && (
                    <input
                      type="text"
                      required
                      autoFocus
                      value={formData.section}
                      onChange={e => setFormData(f => ({ ...f, section: e.target.value }))}
                      className={`w-full border rounded-lg px-3 py-2 mt-2 focus:outline-none focus:ring-2 ${formErrors.section ? 'border-red-400 focus:ring-red-400' : 'focus:ring-indigo-500'}`}
                      placeholder="Enter new section"
                    />
                  )}
                  {formErrors.section && <p className="text-xs text-red-500 mt-1">{formErrors.section}</p>}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Academic Year *</label>
                <input
                  type="text"
                  readOnly
                  required
                  value={formData.academicYear}
                  className="w-full border rounded-lg px-3 py-2 bg-gray-100 text-gray-500 cursor-not-allowed focus:outline-none"
                />
                {formErrors.academicYear && <p className="text-xs text-red-500 mt-1">{formErrors.academicYear}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Class Teacher</label>
                <select
                  value={formData.classTeacherId}
                  onChange={e => setFormData({ ...formData, classTeacherId: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Select Teacher (Optional)</option>
                  {teachers.map(t => (
                    <option key={t._id} value={t._id}>{t.name} – {t.employeeId}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subjects</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 border rounded-lg p-3">
                  {Array.from(new Set([
                    ...ALL_SUBJECTS,
                    ...formData.subjects.map(s => s.subjectName)
                  ])).map(subjectName => {
                    const checked = formData.subjects.some(s => s.subjectName === subjectName);
                    return (
                      <label
                        key={subjectName}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition ${checked ? 'bg-indigo-50 border-indigo-300 text-indigo-800' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSubject(subjectName)}
                          className="rounded text-indigo-600 focus:ring-indigo-500"
                        />
                        {subjectName}
                      </label>
                    );
                  })}
                  <label
                    onClick={e => { e.preventDefault(); setShowAddSubject(s => !s); }}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer text-sm transition ${showAddSubject ? 'bg-indigo-50 border-indigo-300 text-indigo-800' : 'border-dashed border-gray-300 text-indigo-600 hover:bg-gray-50'}`}
                  >
                    <input type="checkbox" checked={showAddSubject} readOnly className="rounded text-indigo-600 focus:ring-indigo-500" />
                    + Add New Subject
                  </label>
                </div>
                {showAddSubject && (
                  <div className="flex gap-2 mt-2">
                    <input
                      type="text"
                      autoFocus
                      value={newSubjectName}
                      onChange={e => setNewSubjectName(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addNewSubject())}
                      className="flex-1 border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Enter new subject name"
                    />
                    <button type="button" onClick={addNewSubject} className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700">
                      Add
                    </button>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); setEditingClass(null); }}
                  className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button type="submit" className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700">
                  {editingClass ? "Update" : "Save"} Class
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {/* Bulk Import Modal */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="bg-indigo-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h3 className="text-lg font-bold text-white">Bulk Upload Classes</h3>
              <button onClick={() => setShowImportModal(false)} className="text-white text-2xl hover:text-indigo-200">✕</button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Upload a CSV file with columns: <strong>ClassName, Section</strong> (required), AcademicYear (optional).
                Each row creates one class. Duplicate class+section combinations will fail gracefully.
              </p>
              <button type="button" onClick={downloadTemplate} className="text-sm text-indigo-600 hover:underline">
                Download CSV template
              </button>
              <div>
                <input
                  type="file"
                  accept=".csv"
                  onChange={e => { setImportFile(e.target.files[0] || null); setImportResult(null); setImportError(''); }}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              {importError && <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{importError}</div>}
              {importResult && (
                <div className="space-y-2">
                  <div className="p-3 bg-green-50 border border-green-200 text-green-700 rounded-lg text-sm">
                    {importResult.created} of {importResult.total} class(es) created successfully.
                    {importResult.failed > 0 && ` ${importResult.failed} failed.`}
                  </div>
                  {importResult.failed > 0 && (
                    <div className="max-h-40 overflow-y-auto border rounded-lg divide-y text-sm">
                      {importResult.results.filter(r => r.status === 'error').map(r => (
                        <div key={r.row} className="px-3 py-2 text-red-600">
                          Row {r.row}: {r.error}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setShowImportModal(false)} className="px-4 py-2 border rounded-lg text-gray-600 hover:bg-gray-100">
                  Close
                </button>
                <button
                  type="button"
                  onClick={handleBulkImport}
                  disabled={!importFile || importing}
                  className="px-5 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
                >
                  {importing ? "Uploading..." : "Upload"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminClasses;
